import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import AiStarsIcon from 'ext-ui/svg/icon/AiStarsIcon'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import Box from 'src/components/Box/Box'
import { useQueryExpensePolicy } from 'src/components/ExpensePolicyUploadBox/expensePolicy.queries'
import ExpensePolicyUploadBoxContent from 'src/components/ExpensePolicyUploadBox/ExpensePolicyUploadBoxContent'
import ExpensePolicyUploadBoxForm from 'src/components/ExpensePolicyUploadBox/ExpensePolicyUploadBoxForm'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { useQueryManagerReviewRules } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { selectAppName } from 'src/redux/selectors/session.selectors'

import style from './ExpensePolicyUploadBox.style'

interface ExpensePolicyUploadBoxProps {
  user?: UserListModel
  organization: OrganizationsListModel
}

const ExpensePolicyUploadBox: React.FC<ExpensePolicyUploadBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const { user, organization } = props
  const appName = useSelector(selectAppName)

  const { data: rules, isPending: gettingRules } = useQueryManagerReviewRules({
    orgId: organization.id,
  })
  const { data: policy, isPending: gettingPolicy } = useQueryExpensePolicy({
    orgId: organization.id,
    enabled: !!rules?.policyAssistantEnabled,
  })
  const loadingRules =
    gettingRules || (!!rules?.policyAssistantEnabled && gettingPolicy)
  const [isEditing, setIsEditing] = useState<boolean>(false)

  return (
    <Box
      className={'ExpensePolicyUploadBox'}
      css={style}
      noShadow
      outlineBorder
    >
      {loadingRules ? (
        <LoadingBackgroundPage />
      ) : (
        <div className={'flex-column gap-10'}>
          <div className={'flex-column gap-4'}>
            <div className={'flex-row justify-between align-start'}>
              <div className={'ExpensePolicyUploadBox-title'}>
                {translate('Expense Policy')}
                <Pill color={'new'}>
                  <span className={'flex-row align-center'}>
                    <AiStarsIcon width={14} height={14} />
                    {translate('AI-Powered')}
                  </span>
                </Pill>
              </div>
              {user?.hasOrgAdminPermissions && !isEditing && (
                <Button
                  variant={'text'}
                  onClick={(): void => {
                    setIsEditing(true)
                  }}
                  aria-label={translate('Update')}
                  data-testid={'updateExpensePolicySuggestionsBtn'}
                >
                  {translate('Update')}
                  <SvgUpdate className={'ExpensePolicyUploadBox-updateBtn'} />
                </Button>
              )}
            </div>
            <div className={'flex-column gap-4 font-sm text-secondary'}>
              <span>
                {translate(
                  "Upload your company's spend policy and activate ${appName}'s AI-Powered Policy Assistant. Get real-time policy insights and suggestions on every transaction.",
                  { appName: appName },
                )}
              </span>
            </div>
          </div>
          {isEditing ? (
            <ExpensePolicyUploadBoxForm
              rules={rules}
              policy={policy}
              organizationId={organization.id}
              closeForm={(): void => {
                setIsEditing(false)
              }}
            />
          ) : (
            <ExpensePolicyUploadBoxContent rules={rules} policy={policy} />
          )}
        </div>
      )}
    </Box>
  )
}

export default ExpensePolicyUploadBox
